import React, { useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Collapse,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TuneIcon from '@mui/icons-material/Tune';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  NORMALIZATION_PRESETS,
  STRATEGY_LABELS,
  formatDuration,
} from './studioUtils';

const PresetCard = ({ preset, active, onClick }) => {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      sx={{
        cursor: 'pointer',
        position: 'relative',
        p: 1.25,
        borderRadius: 2,
        border: `1.5px solid ${active ? preset.accent : theme.palette.divider}`,
        bgcolor: active ? alpha(preset.accent, 0.1) : 'transparent',
        transition: 'all 120ms ease',
        '&:hover': {
          borderColor: preset.accent,
          bgcolor: alpha(preset.accent, 0.06),
        },
      }}
    >
      {active && (
        <CheckCircleIcon
          sx={{ position: 'absolute', top: 8, right: 8, fontSize: 16, color: preset.accent }}
        />
      )}
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: preset.accent }} />
        <Typography variant="body2" fontWeight={700}>
          {preset.name}
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: preset.accent, fontWeight: 600, display: 'block' }}>
        {preset.tagline}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}>
        {preset.description}
      </Typography>
    </Box>
  );
};

const StrategyEditor = ({ label, strategy, onChange, options }) => (
  <Stack spacing={1}>
    <TextField
      select
      size="small"
      label={`${label} · primary`}
      value={strategy.primary_strategy || ''}
      onChange={(e) => onChange({ ...strategy, primary_strategy: e.target.value })}
      fullWidth
    >
      {options.map((o) => (
        <MenuItem key={o.id} value={o.id}>
          {o.name}
        </MenuItem>
      ))}
    </TextField>
    <Autocomplete
      multiple
      size="small"
      options={options.map((o) => o.id)}
      value={strategy.fallback_strategies || []}
      getOptionLabel={(id) => STRATEGY_LABELS[id] || id}
      onChange={(_e, vals) => onChange({ ...strategy, fallback_strategies: vals })}
      renderTags={(vals, getTagProps) =>
        vals.map((id, index) => (
          <Chip
            {...getTagProps({ index })}
            key={id}
            size="small"
            label={`${index + 1}. ${STRATEGY_LABELS[id] || id}`}
          />
        ))
      }
      renderInput={(params) => (
        <TextField {...params} label={`${label} · fallbacks (in order)`} />
      )}
    />
  </Stack>
);

const NormalizationControl = ({
  presetId,
  onSelectPreset,
  advancedOpen,
  onToggleAdvanced,
  interval,
  tolerance,
  onChangeInterval,
  onChangeTolerance,
  globalStrategy,
  onChangeGlobalStrategy,
  perField,
  onChangePerField,
  fieldRows,
  strategyOptions,
}) => {
  const theme = useTheme();

  const options = useMemo(() => {
    if (strategyOptions && strategyOptions.length) return strategyOptions;
    return Object.keys(STRATEGY_LABELS).map((id) => ({ id, name: STRATEGY_LABELS[id] }));
  }, [strategyOptions]);

  const fieldByKey = useMemo(() => {
    const m = {};
    (fieldRows || []).forEach((r) => {
      m[r.key] = r;
    });
    return m;
  }, [fieldRows]);

  const perFieldEntries = Object.entries(perField || {});
  const overrideKeys = new Set(perFieldEntries.map(([k]) => k));

  const addOverride = (key) => {
    if (!key) return;
    onChangePerField({
      ...perField,
      [key]: {
        primary_strategy: globalStrategy.primary_strategy,
        fallback_strategies: [...(globalStrategy.fallback_strategies || [])],
      },
    });
  };
  const removeOverride = (key) => {
    const next = { ...perField };
    delete next[key];
    onChangePerField(next);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        <AutoFixHighIcon fontSize="small" sx={{ color: 'primary.main' }} />
        <Typography variant="subtitle2" fontWeight={700}>
          Fill gaps
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
        Real data has holes. Pick how we line everything up onto one clean
        timeline — every recipe guarantees a complete table with no empty cells.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1,
        }}
      >
        {NORMALIZATION_PRESETS.map((p) => (
          <PresetCard
            key={p.id}
            preset={p}
            active={presetId === p.id}
            onClick={() => onSelectPreset(p.id)}
          />
        ))}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 1.25 }}
      >
        <Chip
          size="small"
          icon={<TuneIcon sx={{ fontSize: 14 }} />}
          label={`every ${formatDuration(interval)} · ±${formatDuration(tolerance)}`}
          sx={{ height: 24, fontWeight: 600 }}
        />
        <Button
          size="small"
          onClick={onToggleAdvanced}
          endIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ textTransform: 'none' }}
        >
          Advanced
        </Button>
      </Stack>

      <Collapse in={advancedOpen}>
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.text.primary, 0.02),
          }}
        >
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Timeline resolution
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <TextField
              size="small"
              type="number"
              label="Interval (s)"
              value={interval}
              onChange={(e) => onChangeInterval(Math.max(1, parseInt(e.target.value, 10) || 1))}
              helperText={formatDuration(interval)}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label="Tolerance (s)"
              value={tolerance}
              onChange={(e) => onChangeTolerance(Math.max(1, parseInt(e.target.value, 10) || 1))}
              helperText={formatDuration(tolerance)}
              fullWidth
            />
          </Stack>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Global strategy
          </Typography>
          <Box sx={{ mt: 1 }}>
            <StrategyEditor
              label="All signals"
              strategy={globalStrategy}
              onChange={onChangeGlobalStrategy}
              options={options}
            />
          </Box>

          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Per-signal overrides
            </Typography>
          </Stack>
          <Autocomplete
            size="small"
            options={(fieldRows || []).map((r) => r.key).filter((k) => !overrideKeys.has(k))}
            getOptionLabel={(k) => fieldByKey[k]?.display_name || fieldByKey[k]?.original_key || k}
            value={null}
            blurOnSelect
            clearOnBlur
            onChange={(_e, val) => addOverride(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Add a signal-specific recipe…"
                sx={{ mt: 1 }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <AddIcon sx={{ fontSize: 16, ml: 0.5, color: 'text.secondary' }} />,
                }}
              />
            )}
          />
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            {perFieldEntries.map(([key, strat]) => (
              <Box
                key={key}
                sx={{ p: 1.25, borderRadius: 1.5, border: `1px solid ${theme.palette.divider}` }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {fieldByKey[key]?.display_name || fieldByKey[key]?.original_key || key}
                  </Typography>
                  <Tooltip title="Remove override">
                    <IconButton size="small" onClick={() => removeOverride(key)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <StrategyEditor
                  label="This signal"
                  strategy={strat}
                  onChange={(s) => onChangePerField({ ...perField, [key]: s })}
                  options={options}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default NormalizationControl;
